<script setup>
import { ref, computed, onMounted } from "vue";
import api from "../api/axios"
import DataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";

import ArgonButton from "@/components/ArgonButton.vue";
import ArgonInput from "@/components/ArgonInput.vue";

// =======================
// STATE
// =======================
const users = ref([]);
const roles = ref([]);

const search = ref("");
const roleFilter = ref("");
const statusFilter = ref("");

const showModal = ref(false);
const isEdit = ref(false);

const form = ref({
  id: null,
  email: "",
  name: "",
  password: "",
  roleId: "",
  active: true
});

// =======================
// TABLE HEADERS
// =======================
const headers = [
  { text: "ID", value: "id" },
  { text: "Email", value: "email" },
  { text: "Nombre", value: "name" },
  { text: "Rol", value: "role" },
  { text: "Estado", value: "active" },
  { text: "Acciones", value: "actions", sortable: false }
];

// =======================
// FILTROS
// =======================
const filteredUsers = computed(() => {
  return users.value.filter(u => {
    const matchSearch =
      u.email.toLowerCase().includes(search.value.toLowerCase()) ||
      (u.name || "").toLowerCase().includes(search.value.toLowerCase());

    const matchRole = roleFilter.value
      ? u.role === roleFilter.value
      : true;

    const matchStatus =
      statusFilter.value === ""
        ? true
        : statusFilter.value === "activo"
        ? u.active
        : !u.active;

    return matchSearch && matchRole && matchStatus;
  });
});

// =======================
// API
// =======================
const getUsers = async () => {
  const res = await api.get("/users");
  users.value = res.data.users;
};

const getRoles = async () => {
  const res = await api.get("/roles");
  roles.value = res.data.roles;
};

const saveUser = async () => {
  if (isEdit.value) {
    await api.put(`/users/${form.value.id}`, form.value);
  } else {
    await api.post(`/users`, form.value);
  }

  showModal.value = false;
  resetForm();
  getUsers();
};

const editUser = (user) => {
  form.value = {
    ...user,
    roleId: user.role_id,
    password: ""
  };
  isEdit.value = true;
  showModal.value = true;
};

const deactivateUser = async (id) => {
  if (!confirm("¿Desactivar usuario?")) return;
  await api.delete(`/users/${id}`);
  getUsers();
};

const openCreate = () => {
  resetForm();
  isEdit.value = false;
  showModal.value = true;
};

const resetForm = () => {
  form.value = {
    id: null,
    email: "",
    name: "",
    password: "",
    roleId: "",
    active: true
  };
};

// =======================
onMounted(() => {
  getUsers();
  getRoles();
});
</script>

<template>
  <div class="container-fluid py-4">

    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0">
        <i class="bi bi-people-fill me-2"></i>
        Gestión de Usuarios
      </h4>

      <argon-button color="success" @click="openCreate">
        <i class="bi bi-plus-circle me-1"></i>
        Nuevo usuario
      </argon-button>
    </div>

    <!-- FILTROS -->
    <div class="card mb-3">
      <div class="card-body row g-3">

        <div class="col-md-4">
          <label>Buscar</label>
          <argon-input v-model="search" placeholder="Email o nombre..." />
        </div>

        <div class="col-md-3">
          <label>Rol</label>
          <select v-model="roleFilter" class="form-control">
            <option value="">Todos</option>
            <option v-for="r in roles" :key="r.id">
              {{ r.name }}
            </option>
          </select>
        </div>

        <div class="col-md-3">
          <label>Estado</label>
          <select v-model="statusFilter" class="form-control">
            <option value="">Todos</option>
            <option value="activo">Activo</option>
            <option value="inactivo">Inactivo</option>
          </select>
        </div>

      </div>
    </div>

    <!-- TABLA -->
    <div class="card">
      <div class="card-body">

 <DataTable
  :headers="headers"
  :items="filteredUsers"
  :rows-per-page="10"
  :rows-items="[5,10,20,50]"
  table-class-name="table table-striped"

  :pagination-options="{
    rowsPerPageText: 'Filas por página',
    rangeSeparatorText: 'de',
    selectLabel: 'Filas',
    selectSuffix: '',
    paginationLabel: ({ start, end, total }) => `${start}-${end} de ${total}`
  }"

  :table-locale="{
    noDataAvailable: 'No hay datos disponibles',
    noResults: 'No se encontraron resultados'
  }"
>

          <!-- Estado -->
          <template #item-active="{ active }">
            <span
              class="badge"
              :class="active ? 'bg-success' : 'bg-secondary'"
            >
              {{ active ? "Activo" : "Inactivo" }}
            </span>
          </template>

          <!-- Acciones -->
          <template #item-actions="{ id, ...user }">
            <button
              class="btn btn-warning btn-sm me-2"
              @click="editUser({ id, ...user })"
            >
              <i class="bi bi-pencil"></i>
            </button>

            <button
              class="btn btn-danger btn-sm"
              @click="deactivateUser(id)"
            >
              <i class="bi bi-person-x"></i>
            </button>
          </template>

        </DataTable>

      </div>
    </div>

    <!-- MODAL -->
    <div v-if="showModal" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">

          <div class="modal-header">
            <h5>
              <i class="bi" :class="isEdit ? 'bi-pencil' : 'bi-person-plus'"></i>
              {{ isEdit ? " Editar usuario" : " Crear usuario" }}
            </h5>
            <button class="btn-close" @click="showModal = false"></button>
          </div>

          <div class="modal-body">

            <div class="mb-3">
              <label>Email</label>
              <argon-input v-model="form.email" />
            </div>

            <div class="mb-3">
              <label>Nombre</label>
              <argon-input v-model="form.name" />
            </div>

            <div class="mb-3">
              <label>Password</label>
              <argon-input type="password" v-model="form.password" />
            </div>

            <div class="mb-3">
              <label>Rol</label>
              <select v-model="form.roleId" class="form-control">
                <option disabled value="">Seleccione</option>
                <option v-for="r in roles" :key="r.id" :value="r.id">
                  {{ r.name }}
                </option>
              </select>
            </div>

            <div class="form-check">
              <input type="checkbox" v-model="form.active" class="form-check-input" />
              <label class="form-check-label">Activo</label>
            </div>

          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showModal = false">
              Cancelar
            </button>

            <button class="btn btn-success" @click="saveUser">
              <i class="bi bi-save me-1"></i>
              Guardar
            </button>
          </div>

        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-backdrop fade show"></div>

  </div>
</template>